import * as React from "react"
import { ExternalLink, Loader2, ChevronDown } from "lucide-react"
import { Link } from "react-router-dom"
import { Dropdown } from "./dropdown"
import { shippingLabelEndpointPrefix } from "../../config"
import { cn } from "../../utils/cn"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'secondary-delete'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  href?: string
  download?: boolean | string
  showExternalIcon?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'default',
      href,
      download,
      disabled,
      showExternalIcon = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-[15px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sseeaa focus-visible:ring-offset-2 disabled:opacity-50 cursor-pointer"

    const variantClasses = {
      primary: "bg-btn-primary text-w hover:bg-btn-primary-active active:!bg-btn-primary-active",
      secondary: "bg-w border border-b1 text-b6 hover:bg-b02 hover:text-b hover:border-b2",
      'secondary-delete': "bg-w border border-r text-r hover:bg-r-05 hover:border-r",
    }

    const sizeClasses = {
      default: "h-11 px-4 py-2",
      sm: "h-9 px-3",
      lg: "h-12 px-8",
      icon: "h-10 w-10",
    }

    const classes = cn(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      className
    )

    if (download && href) {
      return (
        <a
          className={cn(classes, (disabled || isLoading) && "opacity-50 pointer-events-none")}
          href={href}
          download={typeof download === "string" ? download : true}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {children}
        </a>
      )
    }

    if (href) {
      const isInternal = href.startsWith('/') || href.startsWith('#')

      if (isInternal) {
        return (
          <Link
            to={href}
            className={cn(classes, (disabled || isLoading) && "opacity-50 pointer-events-none")}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {children}
          </Link>
        )
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(classes, showExternalIcon && "group", (disabled || isLoading) && "opacity-50 pointer-events-none")}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {children}
          {showExternalIcon && (
            <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          )}
        </a>
      )
    }

    return (
      <button className={classes} ref={ref} disabled={disabled || isLoading} {...props}>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </button>
    )
  },
)
Button.displayName = "Button"

export interface ActionButton {
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'secondary' | 'secondary-delete';
  disabled?: boolean;
}

export interface ButtonGroupProps {
  actions: ActionButton[];
  className?: string;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({ actions, className }) => {
  return (
    <div className={cn("flex flex-col gap-2 mt-auto pt-4", className)}>
      {actions.map((action, index) => (
        <Button
          key={index}
          variant={action.variant || 'primary'}
          onClick={action.onClick}
          className="w-full h-10"
          disabled={action.disabled}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
};
ButtonGroup.displayName = "ButtonGroup";

export const SectionHeader = ({ title, action }: { title: string, action?: React.ReactNode }) => (
  <div className="flex items-center gap-2.5 mb-5">
    <h2 className="font-bold text-b text-xl">{title}</h2>
    {action}
  </div>
);

export const HeaderAction = React.memo(({ label, onClick, icon, isDropdown, items }: {
  label: string,
  onClick?: () => void,
  icon?: React.ReactNode,
  isDropdown?: boolean,
  items?: any[]
}) => {
  const trigger = (
    <div
      onClick={onClick}
      className="flex items-center gap-1 transition-all cursor-pointer group select-none px-3 py-1 rounded-full bg-w ring-1 ring-inset ring-b1 hover:ring-b2 hover:bg-b02"
    >
      {icon && React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<any>, {
        className: cn("w-3.5 h-3.5 text-b4 group-hover:text-b transition-colors", (icon as any).props?.className)
      })}
      <span className="text-[13px] text-b6 group-hover:text-b transition-colors">{label}</span>
    </div>
  );

  if (isDropdown && items) {
    return <Dropdown trigger={trigger} items={items} />;
  }
  return trigger;
});
HeaderAction.displayName = "HeaderAction";

export { Button, ButtonGroup }